import React from 'react'
import Snackbar from '@mui/material/Snackbar'
import Alert from '@mui/material/Alert'
import Button from '@mui/material/Button'
import { useRegisterSW } from 'virtual:pwa-register/react'
import { snackbarPlacement } from '../../contexts/snackbar'
import { useTurnActive } from '../../utils/turnActivity'

const topPlacement = snackbarPlacement('top', 64)

/**
 * "This app has been updated" banner.
 *
 * vite-plugin-pwa builds a service worker whose precache manifest changes with
 * every deploy. This polls for a new one every 60s; when the browser has a new
 * worker waiting, `needRefresh` flips and the banner offers a one-click swap to
 * the fresh bundle (SKIP_WAITING + reload) — no hard-refresh dance.
 *
 * It never reloads by itself, and while an agent turn is answering it offers
 * no button at all: a reload mid-answer would cut the stream. It says the new
 * version will load after the chat finishes, and the button comes back then.
 */
export const UpdateBanner: React.FC = () => {
  const {
    needRefresh: [needRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_url, registration) {
      if (registration) {
        setInterval(() => registration.update(), 60 * 1000)
      }
    },
  })

  const turnActive = useTurnActive()

  if (!needRefresh) return null

  return (
    // Mounted above the router/account providers, so it can't use
    // useSnackbarTopOffset: clear the 64px app bar, the common case.
    <Snackbar
      open
      anchorOrigin={ topPlacement.anchorOrigin }
      sx={{ zIndex: (theme) => theme.zIndex.modal + 1, ...topPlacement.sx }}
    >
      <Alert
        severity="info"
        variant="filled"
        icon={false}
        action={turnActive ? undefined : (
          <Button
            color="inherit"
            size="small"
            sx={{ fontWeight: 600 }}
            onClick={() => updateServiceWorker(true)}
          >
            Update
          </Button>
        )}
      >
        {turnActive
          ? 'Platinum has been updated. You can reload once your chat has finished.'
          : 'Platinum has been updated — click to load the new version.'}
      </Alert>
    </Snackbar>
  )
}

export default UpdateBanner
